import { ReactElement, useEffect, useState } from 'react'

import styled from '@emotion/styled'
import { Controller, useFormContext } from 'react-hook-form'
import { MultiValue, SingleValue } from 'react-select'

import { FieldLabel } from '@cash-design-system/react'

import { FieldErrorText } from '../FieldErrorText'
import { DropdownInput } from './DropdownInput'
import { MultiDropdown } from './MultiDropdown'

const StyledFieldLabel = styled(FieldLabel)`
  min-width: 0;
`

type DropdownOption = {
  value: string
  inputLabel: ReactElement
}

// Common props shared between single and multi-select dropdowns
type BaseDropdownProps<T extends DropdownOption> = {
  name: string
  label?: string
  autoFocus?: boolean
  options: T[]
  placeholder?: string
  ariaLabel?: string
  className?: string
  rules?: React.ComponentProps<typeof Controller>['rules']
  getDefaultValue?: () => T[] | null
}

// Props specific to single-select dropdown
type SingleSelectProps<T extends DropdownOption> = {
  isMulti?: false
  handleChange?: (option: T | null, action: string, onChange?: (value: SingleValue<T>) => void) => void
}

// Props specific to multi-select dropdown
type MultiSelectProps<T extends DropdownOption> = {
  isMulti: true
  handleChange?: (options: T[] | null, action: string, onChange?: (value: MultiValue<T>) => void) => void
}

export type FormDropdownInputProps<T extends DropdownOption> = BaseDropdownProps<T> &
  (SingleSelectProps<T> | MultiSelectProps<T>)

/**
 * @deprecated Use FormInputDropdown instead
 */
export const FormDropdownInput = <
  T extends {
    value: string
    inputLabel: ReactElement
    labelCopy: string
  },
>(
  props: FormDropdownInputProps<T>
) => {
  const {
    name,
    label = '',
    autoFocus,
    options,
    handleChange,
    getDefaultValue,
    placeholder,
    ariaLabel,
    className,
    rules,
    isMulti,
  } = props

  const {
    control,
    register,
    getValues,
    watch,
    formState: { errors, isSubmitted },
  } = useFormContext()

  const singleHandleChange = handleChange as SingleSelectProps<T>['handleChange']

  // For single-select dropdowns
  const [currentOption, setCurrentOption] = useState<T | null>(() => {
    const defaultValue = getValues()[name]
    const defaultOption = options.find(option => option.value === defaultValue)
    return defaultOption ?? null
  })

  // For multi-select dropdowns
  const [currentOptions, setCurrentOptions] = useState<T[] | null>(() => {
    // for cases where it needs special handling to get the default values
    if (getDefaultValue) {
      return getDefaultValue()
    }

    const defaultValues = getValues()[name]
    if (!defaultValues) {
      return null
    }
    const defaultOptions = options.filter(option => defaultValues.includes(option.value))
    return defaultOptions.length > 0 ? defaultOptions : null
  })

  register(name, { ...rules })

  const selectedOption = watch(name)

  useEffect(() => {
    if (isMulti) {
      const selectedOptionValues = selectedOption?.map((option: T) => option.value)
      const newSelectedOptions = selectedOptionValues
        ? options.filter(option => selectedOptionValues.includes(option.value))
        : null

      setCurrentOptions(newSelectedOptions)
    } else {
      const newSelectedOption = options.find(option => option.value === selectedOption) || null
      setCurrentOption(newSelectedOption)
    }
  }, [selectedOption, options, isMulti, handleChange])

  const error = errors[name]
  const showError = error && (error?.type !== 'required' || isSubmitted)
  return (
    <StyledFieldLabel label={label}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          if (isMulti) {
            return (
              <MultiDropdown
                field={field}
                options={options}
                formValue={currentOptions}
                handleChange={(options: T[] | null, action: string, onChange?: (value: MultiValue<T>) => void) => {
                  setCurrentOptions(options)
                  if (props.handleChange) {
                    // defaults to the parent for calling onchange
                    props.handleChange?.(options, action, onChange)
                  } else {
                    field.onChange(options)
                  }
                }}
                autoFocus={autoFocus}
                placeholder={placeholder}
                ariaLabel={ariaLabel ?? label}
                className={className}
                isError={showError}
              />
            )
          }

          return (
            <DropdownInput
              field={field}
              options={options}
              formValue={currentOption}
              handleChange={(
                option: T | null,
                action: string,
                onChange?: ((value: SingleValue<T>) => void) | undefined
              ) => {
                setCurrentOption(option)
                singleHandleChange?.(option, action, onChange)
                // call field onChange for validation
                field.onChange(option?.value)
              }}
              autoFocus={autoFocus}
              placeholder={placeholder}
              ariaLabel={ariaLabel ?? label}
              className={className}
              isError={showError}
            />
          )
        }}
      />
      {showError && <FieldErrorText name={name} />}
    </StyledFieldLabel>
  )
}
